import {
  AssetPermission,
  PermissionName
} from '../../../../components/FlowTeams/types/asset-permissions.types'
import { Box, Text, theme } from '@dtx-company/shared-components/src'
import { ShareWithEveryoneControls } from './ShareWithEveryoneControls'
import { TableRow } from './TableRow'
import { useAssetPermissionsState } from '../../hooks/asset-permissions/useAssetPermissionsState'
import { useAuthState } from '../../../../hooks/useAuthState'
import { useCurrentUsersAssetPermissions } from '../../hooks/asset-permissions/useCurrentUsersAssetPermissions'
import React, { useMemo } from 'react'

export interface AssetPermissionsTableProps {
  assetId: string
  subdomainName?: string
  isWhitelabel?: boolean
  isCodeSubdomain?: boolean
  trackingClickPermission?: (perm: string, assetId?: string) => void
  trackingClickPermissionDropdown?: (assetId: string) => void
  trackingClickShareWithEveryone?: (assetId: string, checked: boolean) => void
}

export const AssetPermissionsTable: React.FC<AssetPermissionsTableProps> = ({
  assetId,
  isWhitelabel,
  isCodeSubdomain,
  subdomainName,
  trackingClickPermission,
  trackingClickPermissionDropdown,
  trackingClickShareWithEveryone
}) => {
  const { permissionsDisplayed, showShareWithEveryoneControls } = useAssetPermissionsTableState(
    assetId,
    isWhitelabel,
    isCodeSubdomain
  )
  return (
    <>
      <AssetPermissionsTableHeader isWhitelabel={isWhitelabel} />
      <Box display="flex" flexDirection="column" width="100%">
        {permissionsDisplayed?.map(p => (
          <TableRow
            assetId={assetId}
            permission={p}
            key={p.entityId}
            isWhitelabel={isWhitelabel}
            isCodeSubdomain={isCodeSubdomain}
            trackingClickPermission={trackingClickPermission}
            trackingClickPermissionDropdown={trackingClickPermissionDropdown}
          />
        ))}
      </Box>
      {showShareWithEveryoneControls && (
        <Box width="100%" height="1px" backgroundColor={theme.colors.secondary.border} mb="16px" />
      )}
      {showShareWithEveryoneControls && (
        <ShareWithEveryoneControls
          assetId={assetId}
          subdomainName={subdomainName}
          isWhitelabel={isWhitelabel}
          isCodeSubdomain={isCodeSubdomain}
          trackingClickShareWithEveryone={trackingClickShareWithEveryone}
          trackingClickPermission={trackingClickPermission}
          trackingClickPermissionDropdown={trackingClickPermissionDropdown}
        />
      )}
    </>
  )
}

export function useAssetPermissionsTableState(
  assetId: string,
  isWhitelabel?: boolean,
  isCodeSubdomain?: boolean
): AssetPermissionsTableState {
  const assetPermissions = useAssetPermissionsState(assetId, isWhitelabel, isCodeSubdomain)
  const currentUserAssetPermissions = useCurrentUsersAssetPermissions(
    assetId,
    isWhitelabel,
    isCodeSubdomain
  )
  const draftPermissions = assetPermissions?.draftPermissions
  const { jwt, hasOperation } = useAuthState()
  const disableShareWithEveryone = hasOperation('hide:share_asset_orgwide')
  // For Whitelabel domains, the owner concept is not in use, so filter out the first result
  const permissionsDisplayed = useMemo(
    () =>
      draftPermissions?.filter(
        (p: AssetPermission, idx) => p.entityId !== jwt?.org?.orgId && (!isWhitelabel || idx !== 0)
      ) ?? [],
    [jwt, draftPermissions, isWhitelabel]
  )

  return {
    permissionsDisplayed,
    showShareWithEveryoneControls:
      !disableShareWithEveryone && currentUserAssetPermissions !== PermissionName.VIEWER,
    currentUserAssetPermissions
  }
}

interface AssetPermissionsTableState {
  permissionsDisplayed: AssetPermission[]
  showShareWithEveryoneControls: boolean
  currentUserAssetPermissions: PermissionName | null
}

const AssetPermissionsTableHeader: React.FC<{ isWhitelabel?: boolean }> = ({ isWhitelabel }) => (
  <>
    <Box justifyContent="space-between" pb="8px" pr="16px">
      <Text fontSize="12px" color="primary.grey">
        Member/Team
      </Text>
      <Text fontSize="12px" color="primary.grey">
        {isWhitelabel ? 'Permissions' : 'Permission'}
      </Text>
    </Box>
    <Box width="100%" height="1px" backgroundColor={theme.colors.secondary.border} mb="16px" />
  </>
)
